import React, { useState } from 'react';
/**
 * LiveRulebookCard — Renders a single ModuleRulebook from the live
 * RULEBOOKS_REGISTRY (governance/rulebooks). Phase 3 Strak-Trek.
 *
 * Replaces the static markdown-metadata RulebookCard for module rulebooks.
 * Every section here is sourced from executable code — what you see is what
 * the pipeline actually enforces.
 *
 * Also surfaces a "Toekomstig (roadmap)" panel sourced from
 * src/governance/rulebooks/roadmaps.ts — aspirational future question types
 * and visuals that aren't enforced yet but signal the design trajectory.
 */
import { ChevronDownIcon, ChevronRightIcon, BookOpenIcon, CheckCircleIcon, TypeIcon, AlertTriangleIcon, PaletteIcon, LayersIcon, SparklesIcon } from 'lucide-react';
import type { ModuleRulebook } from '../../../../governance';
import { getModuleRoadmap } from '../../../../governance';
import { RULEBOOK_COLOR_MAP } from './governanceRulesData';
type Props = {
  rulebook: ModuleRulebook;
  color: string;
};
export function LiveRulebookCard({
  rulebook,
  color
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const c = RULEBOOK_COLOR_MAP[color] ?? RULEBOOK_COLOR_MAP.blue;
  const notationFlags: string[] = [];
  if (rulebook.notation.requireLatexFractions) notationFlags.push('LaTeX-breuken');
  if (rulebook.notation.requireCommaDecimals) notationFlags.push('Komma-decimalen');
  if (rulebook.notation.requireSuperscriptUnits) notationFlags.push('Superscript-eenheden (m², m³)');
  if (rulebook.notation.requireEuroComma) notationFlags.push('Euro-komma');
  const roadmap = getModuleRoadmap(rulebook.moduleId);
  return <div className={`rounded-lg border ${c.border} bg-white overflow-hidden`} data-id="element-4175">
      <button className="flex w-full items-start gap-3 p-4 text-left hover:bg-gray-50 transition-colors" onClick={() => setExpanded(!expanded)} aria-expanded={expanded} data-id="element-4176">
        <BookOpenIcon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${c.badge.split(' ')[1]}`} data-id="element-4177" />
        <div className="min-w-0 flex-1" data-id="element-4178">
          <div className="flex flex-wrap items-center gap-2 mb-1" data-id="element-4179">
            <span className="font-semibold text-gray-800" data-id="element-4180">
              {rulebook.displayName}
            </span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${c.badge}`} data-id="element-4181">
              {rulebook.shortCode}
            </span>
            <span className="rounded-full px-2 py-0.5 text-[10px] font-medium bg-emerald-50 text-emerald-700 border border-emerald-100" data-id="element-4182">
              live
            </span>
            {roadmap && (roadmap.futureQuestionTypes.length > 0 || roadmap.futureVisuals.length > 0) && <span className="rounded-full px-2 py-0.5 text-[10px] font-medium bg-violet-50 text-violet-700 border border-violet-100 flex items-center gap-1" data-id="element-4183">
                  <SparklesIcon className="h-3 w-3" data-id="element-4184" />
                  {roadmap.futureQuestionTypes.length + roadmap.futureVisuals.length}{' '}
                  toekomstig
                </span>}
          </div>
          <p className="text-sm text-gray-600 mt-1" data-id="element-4185">
            {rulebook.didactics.coreSummary}
          </p>
          <div className="flex flex-wrap gap-1.5 mt-2" data-id="element-4186">
            {rulebook.didactics.principles.slice(0, 4).map(p => <span key={p} className="rounded px-1.5 py-0.5 text-[10px] font-mono bg-gray-50 text-gray-500 border border-gray-100" data-id="element-4187">
                {p}
              </span>)}
          </div>
        </div>
        <div className="flex-shrink-0 text-gray-400" data-id="element-4188">
          {expanded ? <ChevronDownIcon className="h-4 w-4" data-id="element-4189" /> : <ChevronRightIcon className="h-4 w-4" data-id="element-4190" />}
        </div>
      </button>

      {expanded && <div className={`border-t ${c.border} ${c.bg} px-4 py-3 space-y-4`} data-id="element-4191">
          {/* Key rules */}
          <Section icon={<CheckCircleIcon className="h-4 w-4 text-gray-400" data-id="element-4193" />} title="Kernregels" data-id="element-4192">
            <ul className="space-y-1.5" data-id="element-4194">
              {rulebook.keyRules.map((rule, i) => <li key={i} className="flex items-start gap-2 text-sm text-gray-700" data-id="element-4195">
                  <CheckCircleIcon className="h-4 w-4 flex-shrink-0 mt-0.5 text-gray-400" data-id="element-4196" />
                  {rule}
                </li>)}
            </ul>
          </Section>

          {/* Didactics */}
          <Section icon={<LayersIcon className="h-4 w-4 text-gray-400" data-id="element-4198" />} title="Didactische context" data-id="element-4197">
            <div className="space-y-1.5 text-sm" data-id="element-4199">
              <DetailRow label="Schoolboek-methodes" value={rulebook.didactics.schoolbookReferences.join(', ')} data-id="element-4200" />
              <DetailRow label="Misconceptie-domein" value={<code className="font-mono text-xs" data-id="element-4202">
                    {rulebook.didactics.misconceptionDomain}
                  </code>} data-id="element-4201" />
            </div>
          </Section>

          {/* Notation */}
          {(notationFlags.length > 0 || (rulebook.notation.forbiddenPatterns?.length ?? 0) > 0) && <Section icon={<TypeIcon className="h-4 w-4 text-gray-400" data-id="element-4204" />} title="Notatie" data-id="element-4203">
              {notationFlags.length > 0 && <div className="flex flex-wrap gap-1.5 mb-2" data-id="element-4205">
                  {notationFlags.map(flag => <span key={flag} className="rounded-full px-2 py-0.5 text-xs bg-white border border-gray-200 text-gray-700" data-id="element-4206">
                      {flag}
                    </span>)}
                </div>}
              {rulebook.notation.forbiddenPatterns?.map((p, i) => <p key={i} className="text-xs text-red-600" data-id="element-4207">
                  <code className="font-mono bg-red-50 px-1.5 py-0.5 rounded mr-2" data-id="element-4208">
                    {p.pattern}
                  </code>
                  {p.label}
                </p>)}
            </Section>}

          {/* Visuals */}
          {(rulebook.visuals.preferredAidTypes?.length ?? 0) > 0 && <Section icon={<PaletteIcon className="h-4 w-4 text-gray-400" data-id="element-4210" />} title="Voorkeurs-visuals" data-id="element-4209">
              <div className="flex flex-wrap gap-1.5" data-id="element-4211">
                {rulebook.visuals.preferredAidTypes?.map(v => <span key={v} className="rounded px-1.5 py-0.5 text-xs font-mono bg-white border border-gray-200 text-gray-700" data-id="element-4212">
                    {v}
                  </span>)}
              </div>
              {rulebook.visuals.familyVisualHints && rulebook.visuals.familyVisualHints.length > 0 && <div className="mt-2 space-y-1" data-id="element-4213">
                    {rulebook.visuals.familyVisualHints.map((h, i) => <p key={i} className="text-xs text-gray-600" data-id="element-4214">
                        <code className="font-mono text-[10px] bg-white px-1 py-0.5 rounded mr-1.5" data-id="element-4215">
                          {h.familyIdPattern}
                        </code>
                        {h.note}
                      </p>)}
                  </div>}
            </Section>}

          {/* Question types */}
          {rulebook.questionTypes.preferredTypes.length > 0 && <Section icon={<TypeIcon className="h-4 w-4 text-gray-400" data-id="element-4217" />} title="Voorkeurs-vraagtypes" data-id="element-4216">
              <div className="flex flex-wrap gap-1.5" data-id="element-4218">
                {rulebook.questionTypes.preferredTypes.map(t => <span key={t} className="rounded px-1.5 py-0.5 text-xs font-mono bg-white border border-gray-200 text-gray-700" data-id="element-4219">
                    {t}
                  </span>)}
              </div>
              {rulebook.questionTypes.customTypeRoles && rulebook.questionTypes.customTypeRoles.length > 0 && <div className="mt-2 space-y-1" data-id="element-4220">
                    <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold" data-id="element-4221">
                      Custom-rollen
                    </p>
                    {rulebook.questionTypes.customTypeRoles.map(r => <div key={r.type} className="text-xs text-gray-600 flex items-center gap-2" data-id="element-4222">
                        <code className="font-mono" data-id="element-4223">{r.type}</code>
                        <span className="text-gray-400" data-id="element-4224">·</span>
                        <span data-id="element-4225">{r.didacticRole}</span>
                        <span className="text-gray-400" data-id="element-4226">·</span>
                        <span data-id="element-4227">DOK {r.preferredDok}</span>
                      </div>)}
                  </div>}
            </Section>}

          {/* Distractor traps */}
          {rulebook.distractorTraps.length > 0 && <Section icon={<AlertTriangleIcon className="h-4 w-4 text-gray-400" data-id="element-4229" />} title="Trap-distractors (val-afleiders)" data-id="element-4228">
              <ul className="space-y-1.5" data-id="element-4230">
                {rulebook.distractorTraps.map(trap => <li key={trap.id} className="text-sm" data-id="element-4231">
                    <p className="font-medium text-gray-800" data-id="element-4232">{trap.label}</p>
                    <p className="text-xs text-gray-600" data-id="element-4233">{trap.description}</p>
                  </li>)}
              </ul>
            </Section>}

          {/* Toekomstig (roadmap) — aspirational, not enforced */}
          {roadmap && (roadmap.futureQuestionTypes.length > 0 || roadmap.futureVisuals.length > 0) && <div className="rounded-lg border border-dashed border-violet-200 bg-violet-50/40 p-3" data-id="element-4234">
                <p className="text-xs font-semibold uppercase tracking-wide text-violet-700 mb-2 flex items-center gap-1.5" data-id="element-4235">
                  <SparklesIcon className="h-4 w-4" data-id="element-4236" />
                  Toekomstig — roadmap (niet afgedwongen)
                </p>
                <p className="text-[11px] text-violet-700/80 mb-3 leading-relaxed" data-id="element-4237">
                  Aspirationele vraagtypes en visuals die we voor deze module
                  willen bouwen. Geen pipeline-check, wel ontwerprichting.
                </p>

                {roadmap.futureQuestionTypes.length > 0 && <div className="mb-3" data-id="element-4238">
                    <p className="text-[10px] uppercase tracking-wide text-violet-600 font-semibold mb-1.5 flex items-center gap-1" data-id="element-4239">
                      <TypeIcon className="h-3 w-3" data-id="element-4240" />
                      Vraagtypes ({roadmap.futureQuestionTypes.length})
                    </p>
                    <ul className="space-y-1.5" data-id="element-4241">
                      {roadmap.futureQuestionTypes.map(t => <li key={t.id} className="text-xs text-gray-700 bg-white/60 rounded px-2 py-1.5 border border-violet-100" data-id="element-4242">
                          <div className="flex flex-wrap items-center gap-1.5" data-id="element-4243">
                            <span className="font-medium text-gray-800" data-id="element-4244">
                              {t.label}
                            </span>
                            <code className="font-mono text-[10px] text-violet-600 bg-violet-50 px-1 rounded" data-id="element-4245">
                              {t.id}
                            </code>
                            {t.tierWindow && <span className="text-[10px] text-gray-500" data-id="element-4246">
                                tier {t.tierWindow.min}–{t.tierWindow.max}
                              </span>}
                          </div>
                          <p className="text-[11px] text-gray-600 mt-0.5" data-id="element-4247">
                            {t.rationale}
                          </p>
                          {t.targetFamilyPatterns && t.targetFamilyPatterns.length > 0 && <div className="flex flex-wrap gap-1 mt-1" data-id="element-4248">
                                {t.targetFamilyPatterns.map(p => <code key={p} className="font-mono text-[9px] text-gray-500 bg-gray-50 px-1 rounded" data-id="element-4249">
                                    {p}
                                  </code>)}
                              </div>}
                        </li>)}
                    </ul>
                  </div>}

                {roadmap.futureVisuals.length > 0 && <div data-id="element-4250">
                    <p className="text-[10px] uppercase tracking-wide text-violet-600 font-semibold mb-1.5 flex items-center gap-1" data-id="element-4251">
                      <PaletteIcon className="h-3 w-3" data-id="element-4252" />
                      Visuals ({roadmap.futureVisuals.length})
                    </p>
                    <ul className="space-y-1.5" data-id="element-4253">
                      {roadmap.futureVisuals.map(v => <li key={v.id} className="text-xs text-gray-700 bg-white/60 rounded px-2 py-1.5 border border-violet-100" data-id="element-4254">
                          <div className="flex flex-wrap items-center gap-1.5" data-id="element-4255">
                            <span className="font-medium text-gray-800" data-id="element-4256">
                              {v.label}
                            </span>
                            <code className="font-mono text-[10px] text-violet-600 bg-violet-50 px-1 rounded" data-id="element-4257">
                              {v.id}
                            </code>
                            <span className={`text-[9px] uppercase tracking-wide px-1 rounded ${v.usage === 'stimulus' ? 'bg-blue-50 text-blue-700' : v.usage === 'support' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`} data-id="element-4258">
                              {v.usage}
                            </span>
                          </div>
                          <p className="text-[11px] text-gray-600 mt-0.5" data-id="element-4259">
                            {v.rationale}
                          </p>
                          {v.targetFamilyPatterns && v.targetFamilyPatterns.length > 0 && <div className="flex flex-wrap gap-1 mt-1" data-id="element-4260">
                                {v.targetFamilyPatterns.map(p => <code key={p} className="font-mono text-[9px] text-gray-500 bg-gray-50 px-1 rounded" data-id="element-4261">
                                    {p}
                                  </code>)}
                              </div>}
                        </li>)}
                    </ul>
                  </div>}
              </div>}
        </div>}
    </div>;
}
function Section({
  icon,
  title,
  children
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) {
  return <div data-id="element-4262">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1.5" data-id="element-4263">
        {icon}
        {title}
      </p>
      {children}
    </div>;
}
function DetailRow({
  label,
  value
}: {
  label: string;
  value: React.ReactNode;
}) {
  return <div className="flex items-start gap-2 text-sm" data-id="element-4264">
      <span className="text-gray-500 min-w-[140px]" data-id="element-4265">{label}:</span>
      <span className="text-gray-700" data-id="element-4266">{value}</span>
    </div>;
}